import AppColors from 'general/constants/AppColors';
import { useEffect, useRef, useState, useCallback } from 'react';

function CustomCardMore({
  children,
  overflowHidden = false,
  removeStyle = false,
  shadow = true,
  fullHeight = true,
  borderColor = AppColors.gray[300],
  removeBorderTop = false,
  filter,
  setFilter,
  scrollTargetSelector,
  hasMore = true,
  loadingIndicator = null,
}) {
  const containerRef = useRef(null);
  const scrollTargetRef = useRef(null);
  const isFetchingRef = useRef(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  const fetchMoreData = useCallback(() => {
    if (isFetchingRef.current) return;
    if (!hasMore) return;
    if (!filter || !setFilter) return;

    isFetchingRef.current = true;

    const currentPage = filter?.page || 0;
    const nextPage = currentPage + 1;
    const nextFilter = { ...filter, page: nextPage };

    setFilter(nextFilter);
  }, [hasMore, filter, setFilter]);

  useEffect(() => {
    const target = scrollTargetSelector
      ? document.querySelector(scrollTargetSelector)
      : containerRef.current || window;

    if (!target) return;

    scrollTargetRef.current = target;

    const handleScroll = () => {
      if (isFetchingRef.current || !hasMore) return;

      let scrollTop, scrollHeight, clientHeight;

      if (target === window) {
        scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        scrollHeight = document.documentElement.scrollHeight;
        clientHeight = window.innerHeight;
      } else {
        scrollTop = target.scrollTop;
        scrollHeight = target.scrollHeight;
        clientHeight = target.clientHeight;
      }

      const threshold = 2;

      if (scrollHeight - (scrollTop + clientHeight) <= threshold) {
        fetchMoreData();
      }
    };

    target.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      target.removeEventListener('scroll', handleScroll);
    };
  }, [fetchMoreData, hasMore, scrollTargetSelector]);

  useEffect(() => {
    isFetchingRef.current = false;
  }, [filter]);

  useEffect(() => {
    if (hasInitialized) return;
    if (!setFilter) return;

    if (filter?.page && filter.page >= 0) {
      setHasInitialized(true);
      return;
    }

    setHasInitialized(true);

    const initialFilter = { ...filter, page: 0 };
    setFilter(initialFilter);
  }, [filter, setFilter, hasInitialized]);

  if (removeStyle) {
    return (
      <div className={`${overflowHidden ? 'overflow-hidden' : 'overflow-auto'}`}>
        {children}
        {loadingIndicator}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`
        bg-white
        ${shadow ? 'shadow-sm' : ''}
        ${overflowHidden ? 'overflow-hidden' : 'overflow-auto'}
        ${fullHeight ? 'h-100' : ''}
      `}
      style={{
        borderTopLeftRadius: removeBorderTop ? '0' : '10px',
        borderTopRightRadius: '10px',
        borderBottomLeftRadius: '10px',
        borderBottomRightRadius: '10px',
        border: `1px solid ${borderColor}`,
        borderTop: removeBorderTop ? 'none' : `1px solid ${borderColor}`,
      }}
    >
      {children}
      {loadingIndicator}
    </div>
  );
}

export default CustomCardMore;
